import { useEffect, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { PortalPanelBody, Typography } from "../../design-system";
import type { ErrorSummaryItem, StepperItemData } from "../../design-system";
import { getContributorSection, listAssignments, putContributorSectionState, submitContributorSections } from "../../portal-auth";
import type { AssignmentCreated, ContributorSectionState, SectionAssignmentStatus } from "../../portal-auth";
import { encodeSectionInstanceId } from "../../routing/route-table";
import type { ConfirmedAnswers, SectionInstanceId } from "../../routing/route-table";
import type { OnFilePort } from "../../state/on-file-port";
import { isRenewalSubmittedWriteRefusal } from "../../state/outcome";
import type { PolicyInScope } from "../../state/renewal-summary-port";
import { useOnFileRead } from "../../state/use-on-file-read";
import type { WorkingCopyPort } from "../../state/working-copy-port";
import { expandSectionInstances } from "../../state/section-instances";
import type { SectionInstance } from "../../state/section-instances";
import { getErrorMessage } from "../../validation/error-registry";
import { AssignDialog } from "./AssignDialog";
import type { AssignDialogSection } from "./AssignDialog";
import { confirmSection } from "./confirm";
import { renewalCaptureContent as content } from "./content";
import { readRepeatItems } from "./exposure-answers";
import type { ExposureAnswers } from "./exposure-answers";
import { findAdditionalDetailsQuestion, getExposureSectionSchema } from "./exposure-schema";
import type { ExposureSectionSchema } from "./exposure-schema";
import { validateAdditionalDetailsPage, validateExposureSection } from "./exposure-validation";
import type { DetailsPageRow, ExposureErrorSummaryItem } from "./exposure-validation";
import { additionalDetailsRowTitle } from "./additional-details";
import { getOnFileFieldSpec } from "./on-file-fields";
import { scopeOnFileToInstance } from "./on-file-render";
import type { OnFileRenderContext } from "./on-file-render";
import { ReassignDialog } from "./ReassignDialog";
import { SectionListRoute } from "./SectionListRoute";
import { SectionPageRoute } from "./SectionPageRoute";
import type { SectionPageRouteProps } from "./SectionPageRoute";
import { assignmentKeyFor, buildSectionListRows, everyRowSettled, firstTickId, sectionTitleId } from "./section-status";
import type { SectionListRow } from "./section-status";

export interface ExposuresJourneyRouteProps {
  readonly renewalId: string;
  readonly businessName: string;
  readonly policiesInScope: readonly PolicyInScope[];
  readonly confirmedAnswers: ConfirmedAnswers;
  readonly port: WorkingCopyPort;
  /** TG-5 (m1-exposure-detail-sourcing): the data-on-file read every H2
   * help panel and picker in this journey reads through. */
  readonly onFilePort: OnFilePort;
  readonly isOwner: boolean;
  /** For a contributor: the assignment keys delegated to them. Every other
   * instance is hidden (AC-19). `undefined` for an owner. */
  readonly delegatedSectionIds?: ReadonlySet<string>;
  /** The owner's stepper (AC-19: never shown to a contributor). */
  readonly stepperItems?: readonly StepperItemData[];
  readonly onSectionConfirmed: (sectionId: SectionInstanceId, answers: Record<string, unknown>) => void;
  readonly onSectionWriteIndeterminate: (sectionId: SectionInstanceId) => void;
  readonly onSaveAndFinishLater: () => void;
  /** Owner only: "Continue and submit" (AC-32). */
  readonly onContinueAndSubmit: () => void;
  /** Contributor only: Submit succeeded (AC-19, screen 7 colleague-submitted). */
  readonly onContributorSubmitted: () => void;
  /** The server refused a section or submit request with 403 (AC-19). */
  readonly onNotPermitted: () => void;
  /** m1-portal-ux-fixes Fix D (DEBT-20261023-a7zv): the server refused a
   * write (a section confirm, a draft save, a contributor marker PUT, a
   * contributor submit, an assign, a reassign or a take-back) because the
   * renewal is already submitted — the caller routes the session to the
   * read-only `/renewal-status` view instead of rendering any outcome
   * panel. */
  readonly onRenewalSubmitted: () => void;
  readonly reloadFailureBanner?: ReactNode;
  /** FIX-1 (m1-portal-design-fixes), H2 README.md:185-186: the list
   * view's own "Back to confirm details" link -- owner only, the caller
   * (`App.tsx`'s `makeMultiSectionRoute`) supplies this only for an
   * owner session, never for a contributor. Forwarded to
   * `SectionListRoute` only, never to an opened `SectionPageRoute` (which
   * already renders its own "Back to your sections" link). */
  readonly onBack?: () => void;
}

type View = { readonly kind: "list" } | { readonly kind: "section"; readonly instanceId: SectionInstanceId };

interface ReassignTarget {
  readonly row: SectionListRow;
  readonly sectionAssigneeId: number;
  readonly assigneeName: string;
  readonly assigneeEmail: string;
}

interface AdditionalDetailsSpec {
  readonly parent: DetailsPageRow;
  readonly title: string;
  readonly helpText: string;
}

/** The additional-details page for an instance whose `additionalDetailsPageKey` is set: the
 * parent question, the parent row the page belongs to (from the parent instance's confirmed
 * answers, which added the page, or its draft), and the row's title (H2 `detailHeader*`). */
function findAdditionalDetailsSpec(
  schema: ExposureSectionSchema,
  instance: SectionInstance,
  confirmedAnswers: ConfirmedAnswers,
  draftAnswers: Readonly<Record<string, ExposureAnswers>>,
  onFile: OnFileRenderContext | undefined,
): AdditionalDetailsSpec | undefined {
  const question = findAdditionalDetailsQuestion(schema);
  const page = question?.repeat?.additionalDetailsPage;
  if (question === undefined || page === undefined || instance.additionalDetailsPageKey === undefined) {
    return undefined;
  }
  const parentId = encodeSectionInstanceId({
    sectionId: instance.sectionId,
    ...(instance.policyNumber !== undefined ? { policyNumber: instance.policyNumber } : {}),
  });
  const sources = [confirmedAnswers[parentId] as ExposureAnswers | undefined, draftAnswers[parentId]];
  for (const source of sources) {
    const rows = readRepeatItems(source ?? {}, question.fieldId);
    const index = rows.findIndex((row) => row.key === instance.additionalDetailsPageKey);
    const row = rows[index];
    if (row !== undefined) {
      const parent: DetailsPageRow = { question, row, rowNumber: index + 1 };
      const title = additionalDetailsRowTitle(parent, rows, getOnFileFieldSpec(schema.sectionId, question.fieldId), onFile);
      return { parent, title, helpText: page.helpText };
    }
  }
  return undefined;
}

/** The title H2 uses wherever a section is named on its own, e.g. the
 * assign/reassign dialogs, their status panels, and a contributor's
 * incomplete-section errors. ADR-20261027-81ff: reads the DISPLAY policy
 * number, not the id-bearing one -- every policy-specific section now
 * names its policy here, not only a section type that happens to repeat
 * on this renewal. */
function rowDisplayTitle(row: SectionListRow): string {
  return row.displayPolicyNumber !== undefined ? `${row.title} (${row.displayPolicyNumber})` : row.title;
}

function hasAnyAnswer(answers: ExposureAnswers | undefined): boolean {
  return answers !== undefined && Object.keys(answers).length > 0;
}

/**
 * ADR-20261016-n4sb (SonarQube CRITICAL typescript:S3776 remediation):
 * every action this route can take was previously a `function` DECLARED
 * INSIDE `ExposuresJourneyRoute` itself — Sonar's cognitive-complexity
 * algorithm treats a nested function as a nesting-level increment for
 * everything inside it, so roughly a dozen such declarations, several
 * carrying their own branches, compounded into the component's own
 * score (48, against this gate's threshold). None of them actually
 * needed to be a CLOSURE over component state — every one reads and
 * writes exactly the state and props this context object names
 * explicitly — so they are now top-level functions in this module,
 * parameterised by `JourneyCtx` instead, called from inside the
 * component rather than declared inside it. That removes the nesting
 * penalty entirely: each function's own complexity is scored against
 * itself, not folded into `ExposuresJourneyRoute`'s, and every one of
 * them individually is far below the threshold. `JourneyCtx` is built
 * fresh every render (plain object literal, no memoisation) — cheap for
 * a small set of state values and setters, and setter identity stays
 * stable across renders regardless (React's own `useState` contract),
 * so nothing downstream of this re-creation ever behaves differently
 * from the single-component version this replaces.
 */
interface JourneyCtx {
  readonly renewalId: string;
  readonly policiesInScope: readonly PolicyInScope[];
  readonly confirmedAnswers: ConfirmedAnswers;
  readonly port: WorkingCopyPort;
  /** TG-5: this journey's whole-renewal on-file read -- `handleContinue`
   * scopes it to one instance (`scopeOnFileToInstance`) only where it
   * actually validates a picker field, exactly like `renderSectionView`
   * does for rendering. */
  readonly onFile: ReturnType<typeof useOnFileRead>;
  readonly isOwner: boolean;
  readonly onSectionConfirmed: ExposuresJourneyRouteProps["onSectionConfirmed"];
  readonly onSectionWriteIndeterminate: ExposuresJourneyRouteProps["onSectionWriteIndeterminate"];
  readonly onSaveAndFinishLater: () => void;
  readonly onContributorSubmitted: () => void;
  readonly onNotPermitted: () => void;
  readonly onRenewalSubmitted: () => void;
  readonly isVisible: (instance: SectionInstance) => boolean;
  readonly rows: readonly SectionListRow[];
  readonly draftAnswers: Readonly<Record<string, ExposureAnswers>>;
  readonly ticked: ReadonlySet<string>;
  readonly reassignTarget: ReassignTarget | null;
  readonly setView: Dispatch<SetStateAction<View>>;
  readonly setDraftAnswers: Dispatch<SetStateAction<Readonly<Record<string, ExposureAnswers>>>>;
  readonly setAttempted: Dispatch<SetStateAction<ReadonlySet<string>>>;
  readonly setIsSaving: Dispatch<SetStateAction<boolean>>;
  readonly setSaveOutcome: Dispatch<SetStateAction<{ title: string; message: string } | null>>;
  readonly setAssignments: Dispatch<SetStateAction<readonly SectionAssignmentStatus[]>>;
  readonly setTicked: Dispatch<SetStateAction<ReadonlySet<string>>>;
  readonly setListErrors: Dispatch<SetStateAction<readonly ErrorSummaryItem[]>>;
  readonly setStatusPanelParagraphs: Dispatch<SetStateAction<readonly string[]>>;
  readonly setAssignDialogOpen: Dispatch<SetStateAction<boolean>>;
  readonly setReassignTarget: Dispatch<SetStateAction<ReassignTarget | null>>;
  readonly setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  /** OBJ-R-8/FIND-849: optimistic update on a successful marker PUT, so a
   * contributor's own just-completed row reads "Completed" immediately
   * without waiting for a refetch (see `section-status.ts`'s `statusFor`
   * doc comment). */
  readonly setContributorSectionStates: Dispatch<SetStateAction<ReadonlyMap<string, ContributorSectionState>>>;
  /** Optimistic overlay for a confirm that already succeeded: the
   * `confirmedAnswers` prop only catches up once the App-level reload
   * (`onSectionConfirmed`) re-reads the HTTP working copy, and until then
   * the list would read the just-confirmed row as "to do" -- a Submit in
   * that window was refused by `handleContributorSubmit`'s own guard. */
  readonly setLocallyConfirmed: Dispatch<SetStateAction<ConfirmedAnswers>>;
}

/** AC-15/AC-33, ADR-20261016-w8rn: on arrival, read back any drafts this
 * renewal has "Save and finish later", seeding only instances with no
 * answers yet (never overwriting anything typed this render, and never a
 * section since confirmed) — the draft-read effect's own body, pulled
 * out so that effect's callback stays a short, nesting-free wrapper.
 * Opening a section creates an empty entry for it, so a section opened
 * before this read settles still takes its saved answers. */
function applyDraftSeed(
  draftSections: readonly { sectionId: string; answers: Record<string, unknown> }[],
  confirmedAnswers: ConfirmedAnswers,
  setDraftAnswers: Dispatch<SetStateAction<Readonly<Record<string, ExposureAnswers>>>>,
): void {
  setDraftAnswers((current) => {
    const next = { ...current };
    for (const draft of draftSections) {
      const instanceId = draft.sectionId as SectionInstanceId;
      if (!hasAnyAnswer(next[instanceId]) && confirmedAnswers[instanceId] === undefined) {
        next[instanceId] = draft.answers as ExposureAnswers;
      }
    }
    return next;
  });
}

/** AC-15/AC-33: "Save and finish later" persists every answer given so
 * far, through the save port's draft write -- never a confirm, never
 * counted against `confirmedAnswers`. A port without the optional draft
 * operation (ADR-20261016-w8rn's TS-layer exception) simply does not
 * persist, matching that port's own pre-existing behaviour.
 *
 * OBJ-C-7/FIND-921: a draft write that comes back Failed or Indeterminate
 * must not be reported to the customer as saved. If any instance's write
 * does not Succeed, this stays on the current screen and shows the SAME
 * generic server-error copy this file already uses for a non-confirm
 * write failure (`content.serverErrorTitle`/`serverErrorMessage`, see
 * `handleContinue`'s contributor-marker-PUT failure above) rather than
 * authoring new text -- every typed draft answer is left exactly as it
 * was (`ctx.draftAnswers` is never cleared on any outcome here). */
async function saveAndFinishLater(ctx: JourneyCtx): Promise<void> {
  const writeDraft = ctx.port.writeDraftSection?.bind(ctx.port);
  if (writeDraft !== undefined) {
    const entries = Object.entries(ctx.draftAnswers).filter(([, answers]) => hasAnyAnswer(answers));
    const outcomes = await Promise.all(
      entries.map(([instanceId, answers]) => writeDraft(ctx.renewalId, instanceId, answers)),
    );
    // Fix D (DEBT-20261023-a7zv): any one draft refused because the
    // renewal is already submitted routes straight to `/renewal-status` --
    // never rendered as the generic server-error panel below.
    if (
      outcomes.some(
        (outcome) => outcome.status === "Failed" && isRenewalSubmittedWriteRefusal(outcome.detail.taxonomyCode),
      )
    ) {
      ctx.onRenewalSubmitted();
      return;
    }
    if (outcomes.some((outcome) => outcome.status !== "Succeeded")) {
      // Set both: this is called from the section/details view (renders
      // `saveOutcome` as the H2 outcome panel) and the section-list view
      // (renders `listErrors` via `ErrorSummary`, the same shape
      // `handleContributorSubmit`'s own server-error path already uses) --
      // whichever screen the customer clicked from shows the failure.
      ctx.setSaveOutcome({ title: content.serverErrorTitle, message: content.serverErrorMessage });
      ctx.setListErrors([{ fieldId: "section-list-submit", text: content.serverErrorMessage }]);
      return;
    }
  }
  ctx.setSaveOutcome(null);
  ctx.onSaveAndFinishLater();
}

function backToList(ctx: JourneyCtx): void {
  ctx.setSaveOutcome(null);
  ctx.setView({ kind: "list" });
}

function showInstance(ctx: JourneyCtx, instanceId: SectionInstanceId, baseAnswers: ConfirmedAnswers): void {
  ctx.setDraftAnswers((current) => ({
    ...current,
    [instanceId]: current[instanceId] ?? (baseAnswers[instanceId] as ExposureAnswers | undefined) ?? {},
  }));
  ctx.setSaveOutcome(null);
  ctx.setListErrors([]);
  ctx.setView({ kind: "section", instanceId });
}

async function openInstance(ctx: JourneyCtx, instanceId: SectionInstanceId): Promise<void> {
  if (!ctx.isOwner) {
    // AC-19: the server, not this list, decides whether the section is
    // the contributor's to open.
    const check = await getContributorSection(assignmentKeyFor(instanceId));
    if (!check.ok && check.error.code === "not_permitted") {
      ctx.onNotPermitted();
      return;
    }
  }
  showInstance(ctx, instanceId, ctx.confirmedAnswers);
}

function handleFieldChange(ctx: JourneyCtx, instanceId: SectionInstanceId, key: string, value: unknown): void {
  ctx.setDraftAnswers((current) => ({ ...current, [instanceId]: { ...current[instanceId], [key]: value } }));
}

/** A repeated section type is "confirmed" for the shared routing layer
 * (`computeSectionStates`, keyed on the bare SectionId) only once every
 * instance of it is. Never written when the bare id IS one of the
 * instances: that entry holds the customer's real answers. */
async function writeRollupIfSectionTypeNowComplete(
  ctx: JourneyCtx,
  instance: SectionInstance,
  simulated: ConfirmedAnswers,
  expanded: readonly SectionInstance[],
): Promise<void> {
  const siblings = expanded.filter((candidate) => candidate.sectionId === instance.sectionId);
  if (siblings.some((sibling) => sibling.instanceId === instance.sectionId)) {
    return;
  }
  if (!siblings.every((sibling) => simulated[sibling.instanceId] !== undefined)) {
    return;
  }
  const rollup = { instances: siblings.length };
  const result = await confirmSection(ctx.port, ctx.renewalId, instance.sectionId, rollup, content.continueLabel);
  if (result.status === "Succeeded") {
    ctx.setLocallyConfirmed((current) => ({ ...current, [instance.sectionId]: rollup }));
    ctx.onSectionConfirmed(instance.sectionId, rollup);
  } else if (result.status === "Indeterminate") {
    ctx.onSectionWriteIndeterminate(instance.sectionId);
  } else if (result.status === "RenewalSubmitted") {
    // Fix D (DEBT-20261023-a7zv): never rendered as an outcome -- the
    // per-instance `confirmSection` call just above this one already
    // routes the session to `/renewal-status` on the same refusal, so this
    // call simply follows suit rather than leaving the rollup silently
    // unwritten with no signal at all.
    ctx.onRenewalSubmitted();
  }
}

/** Runs the AC-19 completion-marker PUT a contributor's Continue makes and handles every
 * outcome, including the two `handleContinue` must stop for immediately. Returns `false` when
 * `handleContinue` must return without touching `confirmedAnswers` (not-permitted, a mid-flight
 * submission, or a generic failure -- the outcome panel is already set here); `true` once the
 * marker PUT has succeeded and the caller should proceed to the shared confirmed-answers update.
 * Pulled out of `handleContinue` purely to keep that function a flat sequence (S3776). */
async function markContributorSectionCompleted(
  ctx: JourneyCtx,
  instance: SectionInstance,
): Promise<boolean> {
  const marker = await putContributorSectionState(assignmentKeyFor(instance.instanceId), "completed");
  if (marker.ok) {
    // OBJ-R-8/FIND-849: mark this row's marker state "completed" locally the instant the PUT
    // succeeds, matching what the server now holds -- `statusFor` otherwise has no server-state
    // entry for this key until the next mount-time refetch and would fall through to treating
    // the about-to-be-set `confirmedAnswers` entry below as sufficient on its own, which is
    // exactly the conflation this fix removes.
    ctx.setContributorSectionStates((current) => {
      const next = new Map(current);
      next.set(assignmentKeyFor(instance.instanceId), "completed");
      return next;
    });
    return true;
  }
  if (marker.error.code === "not_permitted") {
    ctx.onNotPermitted();
    return false;
  }
  // Fix D (DEBT-20261023-a7zv): the renewal was submitted between this instance's own
  // confirmSection write (above) and this marker PUT -- never rendered as the generic
  // server-error panel below.
  if (isRenewalSubmittedWriteRefusal(marker.error.code)) {
    ctx.onRenewalSubmitted();
    return false;
  }
  // OBJ-C-11 (2026-10-19): the marker PUT is the AC-19 completion mechanism for a
  // contributor -- the server reads dbo.section_assignment.state, not the working-copy
  // write, to decide whether Submit will accept this section. Any other failure (5xx,
  // network throw -> "unknown", a 409 the server returns) must not advance the journey:
  // the IN-MEMORY `onSectionConfirmed`/rollup in `handleContinue` (OBJ-R-7) still runs only
  // after this PUT succeeds, so `ctx.confirmedAnswers` and the section list stay "to do" for
  // the REST OF THIS SESSION. OBJ-R-8/FIND-849 (2026-10-19) found that incomplete: the
  // `confirmSection` call made before this marker PUT already persisted the answers to the
  // server-side working copy regardless of this marker's outcome, so a reload rebuilds
  // `confirmedAnswers` from that write and would read this row as "Completed" with no
  // further check -- `section-status.ts`'s `contributorState` parameter is the fix: it
  // overrides a stale `confirmedAnswers`-only "completed" read with the server's own
  // marker state, fetched on mount and left untouched by this failure path.
  //
  // OBJ-R-6 (2026-10-19): the draft is NOT deleted on this path (it used to run
  // unconditionally before this marker PUT). Keeping the draft here means the non-read-only
  // page keeps rendering what the colleague already typed, rather than going blank, and the
  // next Continue re-validates against those same answers and goes straight back to this
  // PUT, instead of surfacing five false "select Yes or No" errors against `{}`.
  ctx.setSaveOutcome({ title: content.serverErrorTitle, message: content.serverErrorMessage });
  return false;
}

async function handleContinue(
  ctx: JourneyCtx,
  instance: SectionInstance,
  schema: ExposureSectionSchema,
  isDetailsPage: boolean,
): Promise<void> {
  const answers = ctx.draftAnswers[instance.instanceId] ?? {};
  const detailsSpec = isDetailsPage
    ? findAdditionalDetailsSpec(schema, instance, ctx.confirmedAnswers, ctx.draftAnswers, undefined)
    : undefined;
  const errors = isDetailsPage
    ? detailsSpec === undefined
      ? []
      : validateAdditionalDetailsPage(detailsSpec.parent, answers)
    : validateExposureSection(schema, answers, scopeOnFileToInstance(ctx.onFile, instance.instanceId));
  ctx.setAttempted((current) => new Set(current).add(instance.instanceId));
  if (errors.length > 0) {
    return;
  }

  ctx.setIsSaving(true);
  ctx.setSaveOutcome(null);
  const result = await confirmSection(ctx.port, ctx.renewalId, instance.instanceId, answers, content.continueLabel);
  ctx.setIsSaving(false);

  if (result.status === "RenewalSubmitted") {
    // Fix D (DEBT-20261023-a7zv): never rendered as a save outcome -- the
    // caller routes this session to `/renewal-status` instead.
    ctx.onRenewalSubmitted();
    return;
  }
  if (result.status === "Failed") {
    ctx.setSaveOutcome({ title: result.title, message: result.message });
    return;
  }
  if (result.status === "Indeterminate") {
    ctx.onSectionWriteIndeterminate(instance.instanceId);
    ctx.setSaveOutcome({ title: result.title, message: result.message });
    return;
  }

  if (!ctx.isOwner) {
    const shouldContinue = await markContributorSectionCompleted(ctx, instance);
    if (!shouldContinue) {
      return;
    }
  }

  const simulated: ConfirmedAnswers = { ...ctx.confirmedAnswers, [instance.instanceId]: answers };
  const expanded = expandSectionInstances({ policiesInScope: ctx.policiesInScope }, simulated);
  ctx.setLocallyConfirmed((current) => ({ ...current, [instance.instanceId]: answers }));
  ctx.onSectionConfirmed(instance.instanceId, answers);
  await writeRollupIfSectionTypeNowComplete(ctx, instance, simulated, expanded);

  ctx.setDraftAnswers((current) => {
    const next = { ...current };
    delete next[instance.instanceId];
    return next;
  });

  // AC-15: the next additional-details page or section in order, computed
  // against the list as it stands AFTER this write (a details page this
  // answer selected is inserted directly after it), or the list after
  // the last one. An owner whose every section is now settled moves on to
  // screen 6 only through "Continue and submit" (ADR-20261027-8fd1).
  const visibleAfter = expanded.filter(ctx.isVisible);
  const position = visibleAfter.findIndex((candidate) => candidate.instanceId === instance.instanceId);
  const next = position === -1 ? undefined : visibleAfter[position + 1];
  if (next !== undefined) {
    showInstance(ctx, next.instanceId, simulated);
    return;
  }
  backToList(ctx);
}

function handleToggleTick(ctx: JourneyCtx, assignmentKey: string, checked: boolean): void {
  ctx.setTicked((current) => {
    const next = new Set(current);
    if (checked) {
      next.add(assignmentKey);
    } else {
      next.delete(assignmentKey);
    }
    return next;
  });
  ctx.setListErrors([]);
}

function tickedSections(ctx: JourneyCtx): readonly AssignDialogSection[] {
  return ctx.rows
    .filter((row) => !row.isAdditionalDetailsPage && ctx.ticked.has(row.assignmentKey))
    .map((row) => ({ sectionId: row.assignmentKey, title: rowDisplayTitle(row) }));
}

function handleAssign(ctx: JourneyCtx): void {
  ctx.setStatusPanelParagraphs([]);
  if (tickedSections(ctx).length === 0) {
    ctx.setListErrors([{ fieldId: firstTickId(ctx.rows), text: getErrorMessage("assign.noSectionTicked", "missing") }]);
    return;
  }
  ctx.setListErrors([]);
  ctx.setAssignDialogOpen(true);
}

function handleAssigned(ctx: JourneyCtx, result: AssignmentCreated, assigneeName: string, assigneeEmail: string): void {
  const assignedKeys = new Set(result.sectionIds);
  ctx.setAssignments((current) => [
    ...current.filter((assignment) => !assignedKeys.has(assignment.sectionId)),
    ...result.sectionIds.map((sectionId) => ({
      sectionId,
      sectionAssigneeId: result.sectionAssigneeId,
      assigneeName,
      assigneeEmail,
      state: "assigned" as const,
    })),
  ]);
  ctx.setTicked(new Set());
  ctx.setAssignDialogOpen(false);
}

function handleReassigned(ctx: JourneyCtx, result: AssignmentCreated, assigneeName: string, assigneeEmail: string): void {
  const reassignTarget = ctx.reassignTarget;
  if (reassignTarget === null) {
    return;
  }
  const key = reassignTarget.row.assignmentKey;
  ctx.setAssignments((current) => [
    ...current.filter((assignment) => assignment.sectionId !== key),
    { sectionId: key, sectionAssigneeId: result.sectionAssigneeId, assigneeName, assigneeEmail, state: "assigned" as const },
  ]);
  ctx.setStatusPanelParagraphs([content.statusPanelReassigned(rowDisplayTitle(reassignTarget.row), assigneeName, assigneeEmail)]);
  ctx.setReassignTarget(null);
}

/** REQ-3 (review-comments.json, PR 3887): `handleTakenBack` below only ever
 * filtered `assignments` -- the returned row's status then fell out of
 * `statusFor` over this owner's MOUNT-TIME `draftAnswers` (seeded once by
 * the `readDraftSections` effect above), so a colleague's answers saved
 * after that mount were never in it. The row read "To do" and the section
 * view opened empty until a reload, short of AC-8(a) ("In progress" with
 * the saved answers shown, editable and saveable). Refetches drafts for
 * the renewal through the SAME `port.readDraftSections` call that effect
 * already makes, and seeds them with the same `applyDraftSeed` -- which
 * never overwrites an already-open draft or a confirmed section, so this
 * is safe to call after every take-back. A failed refetch is silent, same
 * as that effect's own failure path (the mount-time read never surfaces an
 * error either): the optimistic row removal in `handleTakenBack` already
 * succeeded and this best-effort follow-up must not undo or flag over it. */
async function refetchDraftsAfterTakeBack(ctx: JourneyCtx): Promise<void> {
  const result = await ctx.port.readDraftSections?.(ctx.renewalId);
  if (result !== undefined && result.status === "Succeeded") {
    applyDraftSeed(result.value.draftSections, ctx.confirmedAnswers, ctx.setDraftAnswers);
  }
}

function handleTakenBack(ctx: JourneyCtx): void {
  const reassignTarget = ctx.reassignTarget;
  if (reassignTarget === null) {
    return;
  }
  const key = reassignTarget.row.assignmentKey;
  ctx.setAssignments((current) => current.filter((assignment) => assignment.sectionId !== key));
  ctx.setStatusPanelParagraphs([content.statusPanelTakenBack(reassignTarget.assigneeName, rowDisplayTitle(reassignTarget.row))]);
  ctx.setReassignTarget(null);
  // REQ-3: fire-and-forget, after the outcome above is already set -- the
  // take-back outcome message and the dialog's own focus behaviour (inside
  // `ReassignDialog`) stay exactly as they were, not gated on this read.
  void refetchDraftsAfterTakeBack(ctx);
}

function incompleteErrors(incompleteRows: readonly SectionListRow[]): readonly ErrorSummaryItem[] {
  return incompleteRows.map((row) => ({
    fieldId: sectionTitleId(row),
    text: content.contributorIncompleteMessage(rowDisplayTitle(row)),
  }));
}

/** The help panel shown above an opened section when it is assigned to, or
 * was completed by, a colleague — `undefined` otherwise. Pulled out of
 * the section-view renderer below purely to keep that function a flat
 * sequence (S3776). */
function sectionStatusPanel(showDelegation: boolean, row: SectionListRow): readonly string[] | undefined {
  if (showDelegation && row.status === "assigned") {
    return [content.assignedHelpPanel(row.assigneeName ?? "")];
  }
  if (showDelegation && row.status === "completed-by-colleague") {
    return [content.completedByColleaguePanel(row.assigneeName ?? "")];
  }
  return undefined;
}

interface SectionViewParams {
  readonly ctx: JourneyCtx;
  readonly view: Extract<View, { kind: "section" }>;
  readonly allInstances: readonly SectionInstance[];
  readonly visibleInstances: readonly SectionInstance[];
  readonly rows: readonly SectionListRow[];
  readonly confirmedAnswers: ConfirmedAnswers;
  readonly draftAnswers: Readonly<Record<string, ExposureAnswers>>;
  readonly attempted: ReadonlySet<string>;
  readonly showDelegation: boolean;
  readonly businessName: string;
  readonly isSaving: boolean;
  readonly saveOutcome: { title: string; message: string } | null;
  readonly reloadFailureBanner?: ReactNode;
  readonly stepperItems?: readonly StepperItemData[] | undefined;
}

interface SectionViewModel {
  readonly instance: SectionInstance;
  readonly schema: ExposureSectionSchema;
  readonly row: SectionListRow;
  readonly detailsSpec: AdditionalDetailsSpec | undefined;
  readonly isDetailsPage: boolean;
  readonly readOnly: boolean;
  readonly statusPanel: readonly string[] | undefined;
  readonly answers: ExposureAnswers;
  readonly onFile: OnFileRenderContext | undefined;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly position: number;
  readonly previousInstance: SectionInstance | undefined;
  readonly total: number;
}

/** Resolves every value `renderSectionView` derives from its params before it can render --
 * `undefined` when the instance/schema/row this view names cannot (yet) be resolved, in which
 * case the caller falls through to the list view exactly as the inline block this replaces did.
 * Pulled out of `renderSectionView` purely to keep that function a flat sequence (S3776). */
function resolveSectionViewModel(params: SectionViewParams): SectionViewModel | undefined {
  const { ctx, view, allInstances, visibleInstances, rows, confirmedAnswers, draftAnswers, attempted, showDelegation } =
    params;

  const instance = allInstances.find((candidate) => candidate.instanceId === view.instanceId);
  const schema = instance !== undefined ? getExposureSectionSchema(instance.sectionId) : undefined;
  const row = rows.find((candidate) => candidate.instanceId === view.instanceId);
  if (instance === undefined || schema === undefined || row === undefined) {
    return undefined;
  }

  const isDetailsPage = instance.additionalDetailsPageKey !== undefined;
  const readOnly = showDelegation && row.status === "assigned";
  const statusPanel = sectionStatusPanel(showDelegation, row);
  const answers = readOnly
    ? ((confirmedAnswers[view.instanceId] as ExposureAnswers | undefined) ?? {})
    : (draftAnswers[view.instanceId] ?? {});
  // An additional-details page reads the parent instance's on-file records only to name the
  // record a replacement replaces.
  const parentInstanceId = encodeSectionInstanceId({
    sectionId: instance.sectionId,
    ...(instance.policyNumber !== undefined ? { policyNumber: instance.policyNumber } : {}),
  });
  const onFile: OnFileRenderContext = scopeOnFileToInstance(ctx.onFile, isDetailsPage ? parentInstanceId : view.instanceId);
  const detailsSpec = isDetailsPage ? findAdditionalDetailsSpec(schema, instance, confirmedAnswers, draftAnswers, onFile) : undefined;
  let errors: readonly ExposureErrorSummaryItem[] = [];
  if (attempted.has(view.instanceId)) {
    if (!isDetailsPage) {
      errors = validateExposureSection(schema, answers, onFile);
    } else if (detailsSpec !== undefined) {
      errors = validateAdditionalDetailsPage(detailsSpec.parent, answers);
    }
  }
  const position = visibleInstances.findIndex((candidate) => candidate.instanceId === view.instanceId);
  // H2 README.md:241: "Back" (secondary), shown "from section 2" -- i.e.
  // whenever a previous entry exists in the SAME ordered list "Section N
  // of M" counts against (`visibleInstances`: the full list for an
  // owner, the delegated-only list for a contributor).
  const previousInstance = position > 0 ? visibleInstances[position - 1] : undefined;
  // H2 counts the additional-details pages the open section's answers add as they are given, so
  // "Section N of M" includes them before Continue.
  const total = readOnly
    ? visibleInstances.length
    : expandSectionInstances({ policiesInScope: ctx.policiesInScope }, { ...confirmedAnswers, [view.instanceId]: answers }).filter(
        ctx.isVisible,
      ).length;

  return { instance, schema, row, detailsSpec, isDetailsPage, readOnly, statusPanel, answers, onFile, errors, position, previousInstance, total };
}

/** The `SectionPageRoute` props `renderSectionView` includes only conditionally, built as one
 * object so the merge-or-omit ternary for each stays out of that function's own complexity count
 * (S3776, `exactOptionalPropertyTypes`: omitting the key, never setting it to `undefined`,
 * matches the inline block this replaces exactly). */
/** AC-25: the owner's section header offers "Assign to a colleague" on a section they hold, or
 * "Reassign" on an assigned one; either returns to the list with that section ticked or its
 * reassign dialog open. */
function sectionHeaderAction(ctx: JourneyCtx, row: SectionListRow): SectionPageRouteProps["headerAction"] {
  if (!ctx.isOwner || row.isAdditionalDetailsPage) {
    return undefined;
  }
  if (row.status === "assigned") {
    const sectionAssigneeId = row.assigneeSectionAssigneeId;
    if (sectionAssigneeId === undefined) {
      return undefined;
    }
    return {
      label: content.reassignLabel,
      testId: "section-page-reassign",
      onClick: () => {
        backToList(ctx);
        ctx.setStatusPanelParagraphs([]);
        ctx.setReassignTarget({
          row,
          sectionAssigneeId,
          assigneeName: row.assigneeName ?? "",
          assigneeEmail: row.assigneeEmail ?? "",
        });
      },
    };
  }
  if (row.status === "completed-by-colleague") {
    return undefined;
  }
  return {
    label: content.assignToColleagueLabel,
    testId: "section-page-assign",
    onClick: () => {
      backToList(ctx);
      ctx.setStatusPanelParagraphs([]);
      ctx.setListErrors([]);
      ctx.setTicked(new Set([row.assignmentKey]));
      ctx.setAssignDialogOpen(true);
    },
  };
}

function sectionPageRouteOptionalProps(
  ctx: JourneyCtx,
  model: SectionViewModel,
  stepperItems: readonly StepperItemData[] | undefined,
  reloadFailureBanner: ReactNode | undefined,
  saveOutcome: { title: string; message: string } | null,
): Partial<SectionPageRouteProps> {
  const { detailsSpec, instance, statusPanel, onFile, previousInstance, schema } = model;
  const headerAction = sectionHeaderAction(ctx, model.row);
  return {
    ...(headerAction !== undefined ? { headerAction } : {}),
    ...(stepperItems !== undefined ? { stepperItems } : {}),
    ...(onFile !== undefined && !model.isDetailsPage ? { onFile } : {}),
    ...(previousInstance !== undefined
      ? { onPrevious: () => void openInstance(ctx, previousInstance.instanceId) }
      : {}),
    ...(reloadFailureBanner !== undefined ? { reloadFailureBanner } : {}),
    ...(model.isDetailsPage
      ? detailsSpec !== undefined
        ? { additionalDetails: detailsSpec }
        : {}
      : { schema }),
    ...(instance.displayPolicyNumber !== undefined ? { policyNumber: instance.displayPolicyNumber } : {}),
    ...(statusPanel !== undefined ? { statusPanelParagraphs: statusPanel } : {}),
    ...(saveOutcome !== null
      ? {
          // Fix A/B (m1-portal-ux-fixes): the H2 square panel, not Alert --
          // `SectionPageRoute` itself renders `saveOutcome` at the top of
          // the card, before the questions (see that component's own
          // comment at the call site).
          saveOutcome: (
            <PortalPanelBody id="exposure-section-outcome" expanded tone="error" title={saveOutcome.title} testId="exposure-section-outcome">
              <Typography variant="body">{saveOutcome.message}</Typography>
            </PortalPanelBody>
          ),
        }
      : {}),
  };
}

/** Renders the opened-section page for `ExposuresJourneyRoute`'s `view.kind
 * === "section"` case, or `undefined` when the instance/schema/row this
 * view names cannot (yet) be resolved -- in which case the caller falls
 * through to the list view exactly as the inline block this replaces did.
 * Factored out so the component itself (ADR-20261016-n4sb) does not fold
 * this block's own branching into its score (S3776). */
function renderSectionView(params: SectionViewParams): ReactNode | undefined {
  const { ctx, view, businessName, isSaving, saveOutcome, reloadFailureBanner, stepperItems } = params;

  const model = resolveSectionViewModel(params);
  if (model === undefined) {
    return undefined;
  }
  const { instance, schema, row, isDetailsPage, readOnly, answers, errors, position, total } = model;

  // ADR-20261027-81ff: the section page's own "Policy number" line below
  // reads the DISPLAY field -- every policy-specific section shows its
  // policy, not only one whose type happens to repeat.
  // Keyed by instance so each section is a new page: it opens at the top (PortalLayout) with its
  // own help panels closed, as H2's Continue does.
  return (
    <SectionPageRoute
      key={instance.instanceId}
      businessName={businessName}
      isOwner={ctx.isOwner}
      title={row.title}
      order={position + 1}
      total={total}
      answers={answers}
      onChange={(key, value) => {
        handleFieldChange(ctx, view.instanceId, key, value);
      }}
      errors={errors}
      onContinue={() => {
        if (readOnly) {
          backToList(ctx);
          return;
        }
        void handleContinue(ctx, instance, schema, isDetailsPage);
      }}
      onBackToList={() => backToList(ctx)}
      readOnly={readOnly}
      isSaving={isSaving}
      onSaveAndFinishLater={() => void saveAndFinishLater(ctx)}
      {...sectionPageRouteOptionalProps(ctx, model, stepperItems, reloadFailureBanner, saveOutcome)}
    />
  );
}

interface ListViewParams {
  readonly ctx: JourneyCtx;
  readonly rows: readonly SectionListRow[];
  readonly isOwner: boolean;
  readonly businessName: string;
  readonly ticked: ReadonlySet<string>;
  readonly onContinueAndSubmit: () => void;
  readonly assignDialogOpen: boolean;
  readonly isSubmitting: boolean;
  readonly listErrors: readonly ErrorSummaryItem[];
  readonly stepperItems?: readonly StepperItemData[] | undefined;
  readonly reassignTarget: ReassignTarget | null;
  readonly statusPanelParagraphs: readonly string[];
  readonly reloadFailureBanner?: ReactNode;
  readonly showDelegation: boolean;
  readonly setAssignDialogOpen: Dispatch<SetStateAction<boolean>>;
  readonly setReassignTarget: Dispatch<SetStateAction<ReassignTarget | null>>;
  readonly setStatusPanelParagraphs: Dispatch<SetStateAction<readonly string[]>>;
  readonly onBack?: () => void;
}

/** Renders the section-list view plus its assign/reassign dialogs --
 * `ExposuresJourneyRoute`'s fallback return once `renderSectionView`
 * returns `undefined`. Factored out for the same S3776 reason as that
 * function. */
function renderListView(params: ListViewParams): ReactNode {
  const {
    ctx,
    rows,
    isOwner,
    businessName,
    ticked,
    onContinueAndSubmit,
    assignDialogOpen,
    isSubmitting,
    listErrors,
    stepperItems,
    reassignTarget,
    statusPanelParagraphs,
    reloadFailureBanner,
    showDelegation,
    setAssignDialogOpen,
    setReassignTarget,
    setStatusPanelParagraphs,
    onBack,
  } = params;

  return (
    <>
      <SectionListRoute
        rows={rows}
        isOwner={isOwner}
        businessName={businessName}
        canContinueAndSubmit={isOwner && everyRowSettled(rows)}
        ticked={ticked}
        {...(onBack !== undefined ? { onBack } : {})}
        onToggleTick={(assignmentKey, checked) => handleToggleTick(ctx, assignmentKey, checked)}
        onOpenSection={(instanceId) => void openInstance(ctx, instanceId as SectionInstanceId)}
        onAssign={() => handleAssign(ctx)}
        onReassign={(row) => {
          if (row.assigneeSectionAssigneeId !== undefined) {
            setStatusPanelParagraphs([]);
            setReassignTarget({
              row,
              sectionAssigneeId: row.assigneeSectionAssigneeId,
              assigneeName: row.assigneeName ?? "",
              assigneeEmail: row.assigneeEmail ?? "",
            });
          }
        }}
        onContinueAndSubmit={onContinueAndSubmit}
        onSubmit={() => void handleContributorSubmit(ctx)}
        assignDialogOpen={assignDialogOpen}
        isSubmitting={isSubmitting}
        errorItems={listErrors}
        {...(stepperItems !== undefined ? { stepperItems } : {})}
        {...(reassignTarget !== null ? { reassignOpenKey: reassignTarget.row.assignmentKey } : {})}
        {...(statusPanelParagraphs.length > 0 ? { statusPanelParagraphs } : {})}
        {...(reloadFailureBanner !== undefined ? { reloadFailureBanner } : {})}
      />
      {showDelegation && assignDialogOpen ? (
        <AssignDialog
          open
          onClose={() => setAssignDialogOpen(false)}
          sections={tickedSections(ctx)}
          onAssigned={(result, assigneeName, assigneeEmail) => handleAssigned(ctx, result, assigneeName, assigneeEmail)}
          onRenewalSubmitted={ctx.onRenewalSubmitted}
        />
      ) : null}
      {showDelegation && reassignTarget !== null ? (
        <ReassignDialog
          open
          onClose={() => setReassignTarget(null)}
          currentSectionAssigneeId={reassignTarget.sectionAssigneeId}
          currentAssigneeName={reassignTarget.assigneeName}
          currentAssigneeEmail={reassignTarget.assigneeEmail}
          section={{ sectionId: reassignTarget.row.assignmentKey, title: rowDisplayTitle(reassignTarget.row) }}
          onReassigned={(result, assigneeName, assigneeEmail) => handleReassigned(ctx, result, assigneeName, assigneeEmail)}
          onTakenBack={() => handleTakenBack(ctx)}
          onRenewalSubmitted={ctx.onRenewalSubmitted}
        />
      ) : null}
    </>
  );
}

async function handleContributorSubmit(ctx: JourneyCtx): Promise<void> {
  const incomplete = ctx.rows.filter((row) => row.status !== "completed");
  if (incomplete.length > 0) {
    ctx.setListErrors(incompleteErrors(incomplete));
    return;
  }
  ctx.setListErrors([]);
  ctx.setIsSubmitting(true);
  const result = await submitContributorSections();
  ctx.setIsSubmitting(false);
  if (result.ok) {
    ctx.onContributorSubmitted();
    return;
  }
  if (result.error.code === "not_permitted") {
    ctx.onNotPermitted();
    return;
  }
  // Fix D (DEBT-20261023-a7zv): never rendered as the generic server-error
  // banner below -- the renewal was submitted (by this session or another)
  // between the completeness check above and this write.
  if (isRenewalSubmittedWriteRefusal(result.error.code)) {
    ctx.onRenewalSubmitted();
    return;
  }
  if (result.error.incompleteSectionIds !== undefined) {
    const refused = new Set(result.error.incompleteSectionIds);
    const matchedRows = ctx.rows.filter((row) => refused.has(row.assignmentKey));
    // OBJ-R-5 (2026-10-19): the server's refused set is the live delegated-section list
    // (`list_active_for_assignee`), not the mint-time JWT claim `ctx.rows` is built from — a
    // section the owner grants to this colleague mid-session can be named here before the
    // client's own claim has widened to include it (the next session refresh re-mints the claim
    // from the live set, ADR-20261019-p8nk, so the window is at most one JWT lifetime).
    // Mapping purely through `ctx.rows` then silently drops any id with no matching row: the
    // error summary showed nothing and Submit appeared to hang, with no way forward short of
    // signing in again. A matched id still gets its own named, per-section error exactly as
    // before; any id refused but absent from `ctx.rows` now surfaces the generic server-error
    // banner instead of nothing, so Submit visibly fails rather than silently doing so. `ctx.rows`
    // itself is not widened here: it follows the claim, which the next refresh re-mints live.
    // OBJ-C-12 (2026-10-19): `matchedRows.length < refused.size` compared a ROW count against an
    // ID count. `ctx.rows` is one row per section INSTANCE, and an additional-details page is its
    // own row sharing its parent's `assignmentKey` (section-status.ts `assignmentKeyFor`), so one
    // refused id can match two rows and mask a second, genuinely unmatched id (e.g. two matched
    // rows against two refused ids, one of which has no row at all). Compare by key instead: every
    // refused id must be covered by at least one row's `assignmentKey`.
    const matchedKeys = new Set<string>(matchedRows.map((row) => row.assignmentKey));
    const hasUnmatchedRefusal = [...refused].some((id) => !matchedKeys.has(id));
    ctx.setListErrors(
      hasUnmatchedRefusal
        ? [...incompleteErrors(matchedRows), { fieldId: "section-list-submit", text: content.serverErrorMessage }]
        : incompleteErrors(matchedRows),
    );
    return;
  }
  ctx.setListErrors([{ fieldId: "section-list-submit", text: content.serverErrorMessage }]);
}

/**
 * H2 screens 4&5 composition root (TG-6): the section list and, when one
 * is opened, its section page, for an owner (full list, assign/reassign,
 * read-only pattern) and a contributor (their delegated sections only,
 * no stepper, eyebrow, checkboxes or assign controls; AC-19).
 */
export function ExposuresJourneyRoute({
  renewalId,
  businessName,
  policiesInScope,
  confirmedAnswers: serverConfirmedAnswers,
  port,
  onFilePort,
  isOwner,
  delegatedSectionIds,
  stepperItems,
  onSectionConfirmed,
  onSectionWriteIndeterminate,
  onSaveAndFinishLater,
  onContinueAndSubmit,
  onContributorSubmitted,
  onNotPermitted,
  onRenewalSubmitted,
  reloadFailureBanner,
  onBack,
}: Readonly<ExposuresJourneyRouteProps>) {
  const [view, setView] = useState<View>({ kind: "list" });
  // TG-5: one whole-renewal on-file read for the whole journey -- every
  // section instance's own context is a narrowed VIEW of this SAME read
  // (`scopeOnFileToInstance`), never a second read per section.
  const onFile = useOnFileRead(onFilePort, renewalId);
  const [draftAnswers, setDraftAnswers] = useState<Readonly<Record<string, ExposureAnswers>>>({});
  const [attempted, setAttempted] = useState<ReadonlySet<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<{ title: string; message: string } | null>(null);
  const [assignments, setAssignments] = useState<readonly SectionAssignmentStatus[]>([]);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [listErrors, setListErrors] = useState<readonly ErrorSummaryItem[]>([]);
  const [statusPanelParagraphs, setStatusPanelParagraphs] = useState<readonly string[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<ReassignTarget | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contributorSectionStates, setContributorSectionStates] = useState<ReadonlyMap<string, ContributorSectionState>>(
    new Map(),
  );
  const [locallyConfirmed, setLocallyConfirmed] = useState<ConfirmedAnswers>({});
  // The reloaded server read wins wherever it already carries an entry.
  const confirmedAnswers: ConfirmedAnswers = { ...locallyConfirmed, ...serverConfirmedAnswers };

  // ADR-20261028-3mxk: delegation is always on now (the build-time flag is
  // retired) -- an owner always sees assign/reassign controls.
  const showDelegation = isOwner;

  // OBJ-R-8/FIND-849: a contributor's own completion state lives on the
  // server's marker (`dbo.section_assignment.state`), not in
  // `confirmedAnswers` -- see `section-status.ts`'s `statusFor` doc
  // comment. Read every delegated section's real state once on mount so a
  // reload after a failed marker PUT does not show "Completed" purely
  // because the working-copy write that failed PUT's `confirmSection`
  // call already persisted. A fetch failure for a key simply leaves it
  // absent from the map, which falls back to the prior
  // `confirmedAnswers`-only behaviour rather than failing the whole list.
  useEffect(() => {
    if (isOwner || delegatedSectionIds === undefined) {
      return undefined;
    }
    let cancelled = false;
    void Promise.all(
      [...delegatedSectionIds].map((key) => getContributorSection(key).then((result) => ({ key, result }))),
    ).then((entries) => {
      if (cancelled) {
        return;
      }
      const settled: Array<readonly [string, ContributorSectionState]> = [];
      for (const entry of entries) {
        if (entry.result.ok) {
          settled.push([entry.key, entry.result.value.state]);
        }
      }
      // OBJ-R-10/FIND-859 (2026-10-19): this mount-time fetch and a
      // marker PUT's optimistic `setContributorSectionStates` update (the
      // block a few lines above `handleContinue`'s `confirmSection` call)
      // race. A full replacement here would drop an optimistic
      // "completed" set after this fetch started but before it settled,
      // reverting a just-completed section back to whatever this
      // (now-stale) read saw. Merge instead, with the current map's
      // entries taking precedence over this settle's -- `current` can
      // only be more recent than the snapshot this effect read at mount.
      setContributorSectionStates((current) => new Map([...settled, ...current]));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per mount/renewal, matching the assignments-read effect above
  }, [isOwner, renewalId]);

  useEffect(() => {
    if (!showDelegation) {
      return undefined;
    }
    let cancelled = false;
    void listAssignments().then((result) => {
      if (!cancelled && result.ok) {
        setAssignments(result.value);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [showDelegation]);

  // AC-15/AC-33, ADR-20261016-w8rn: see `applyDraftSeed`'s own doc
  // comment for what this effect seeds and why.
  useEffect(() => {
    let cancelled = false;
    void port.readDraftSections?.(renewalId).then((result) => {
      if (!cancelled && result.status === "Succeeded") {
        applyDraftSeed(result.value.draftSections, confirmedAnswers, setDraftAnswers);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per mount/renewal, matching the assignments-read effect above
  }, [renewalId]);

  const allInstances: readonly SectionInstance[] = expandSectionInstances({ policiesInScope }, confirmedAnswers);
  const isVisible = (instance: SectionInstance): boolean =>
    delegatedSectionIds === undefined || delegatedSectionIds.has(assignmentKeyFor(instance.instanceId));
  const visibleInstances = allInstances.filter(isVisible);
  const inProgress = new Set(
    Object.entries(draftAnswers)
      .filter(([, answers]) => hasAnyAnswer(answers))
      .map(([instanceId]) => instanceId),
  );
  const rows = buildSectionListRows(
    visibleInstances,
    confirmedAnswers,
    showDelegation ? assignments : [],
    inProgress,
    isOwner ? new Map() : contributorSectionStates,
  );

  const ctx: JourneyCtx = {
    renewalId,
    policiesInScope,
    confirmedAnswers,
    port,
    onFile,
    isOwner,
    onSectionConfirmed,
    onSectionWriteIndeterminate,
    onSaveAndFinishLater,
    onContributorSubmitted,
    onNotPermitted,
    onRenewalSubmitted,
    isVisible,
    rows,
    draftAnswers,
    ticked,
    reassignTarget,
    setView,
    setDraftAnswers,
    setAttempted,
    setIsSaving,
    setSaveOutcome,
    setAssignments,
    setTicked,
    setListErrors,
    setStatusPanelParagraphs,
    setAssignDialogOpen,
    setReassignTarget,
    setIsSubmitting,
    setContributorSectionStates,
    setLocallyConfirmed,
  };

  if (view.kind === "section") {
    const sectionView = renderSectionView({
      ctx,
      view,
      allInstances,
      visibleInstances,
      rows,
      confirmedAnswers,
      draftAnswers,
      attempted,
      showDelegation,
      businessName,
      isSaving,
      saveOutcome,
      reloadFailureBanner,
      stepperItems,
    });
    if (sectionView !== undefined) {
      return sectionView;
    }
  }

  return renderListView({
    ctx,
    rows,
    isOwner,
    businessName,
    ticked,
    onContinueAndSubmit,
    assignDialogOpen,
    isSubmitting,
    listErrors,
    stepperItems,
    reassignTarget,
    statusPanelParagraphs,
    reloadFailureBanner,
    showDelegation,
    setAssignDialogOpen,
    setReassignTarget,
    setStatusPanelParagraphs,
    ...(onBack !== undefined ? { onBack } : {}),
  });
}
